import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { deleteWorkspaceFontFamily, getWorkspaceFonts, uploadWorkspaceFonts } from '@/api/workspaceFonts.api';
import type { WorkspaceCustomFontDto } from '@/api/recipes';
/** Invalidate on sign-out via `queryClient.removeQueries({ queryKey: WORKSPACE_FONTS_QUERY_KEY_ROOT })`. */
export const WORKSPACE_FONTS_QUERY_KEY_ROOT = ['workspace-fonts'] as const;

type UseWorkspaceFontsQueryOptions = {
  enabled: boolean;
};

/**
 * GET `/v1/workspaces/{workspaceId}/fonts`. Disabled when `workspaceId` is missing or `enabled` is false.
 */
export function useWorkspaceFontsQuery(workspaceId: string | undefined, options: UseWorkspaceFontsQueryOptions) {
  return useQuery({
    queryKey: [...WORKSPACE_FONTS_QUERY_KEY_ROOT, workspaceId],
    queryFn: () => getWorkspaceFonts(workspaceId!),
    enabled: Boolean(workspaceId) && options.enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * POST `/v1/workspaces/{workspaceId}/fonts`. Successful rows are merged into the workspace fonts
 * cache so the settings list and compositor pickers update without a reload. A thrown upload
 * refetches the list, because the server may have stored the font after the client stopped waiting.
 */
export function useUploadWorkspaceFontsMutation(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => {
      if (!workspaceId) {
        throw new Error('No active workspace');
      }
      return uploadWorkspaceFonts(workspaceId, files);
    },
    onSuccess: async (result) => {
      if (!workspaceId || result.uploadedFonts.length === 0) {
        return;
      }
      await settleInFlightWorkspaceFontsFetch(queryClient, workspaceId);
      queryClient.setQueryData<WorkspaceCustomFontDto[]>(workspaceFontsQueryKey(workspaceId), (current) =>
        mergeUploadedWorkspaceFonts(current, result.uploadedFonts),
      );
    },
    onError: () => {
      if (!workspaceId) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: workspaceFontsQueryKey(workspaceId) });
    },
  });
}

/**
 * Deletes every face in a family via DELETE `/v1/workspaces/{workspaceId}/fonts?fontFamilyKey=`.
 * Removed rows are dropped from the workspace fonts cache so the settings list and compositor
 * pickers update without a reload. A failure refetches the list.
 */
export function useDeleteWorkspaceFontsMutation(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fontFamilyKey: string) => {
      if (!workspaceId) {
        throw new Error('No active workspace');
      }
      return deleteWorkspaceFontFamily(workspaceId, fontFamilyKey);
    },
    onSuccess: async (_result, fontFamilyKey) => {
      if (!workspaceId || fontFamilyKey.length === 0) {
        return;
      }
      await settleInFlightWorkspaceFontsFetch(queryClient, workspaceId);
      queryClient.setQueryData<WorkspaceCustomFontDto[]>(workspaceFontsQueryKey(workspaceId), (current) =>
        (current ?? []).filter((font) => font.fontFamilyKey !== fontFamilyKey),
      );
    },
    onError: () => {
      if (!workspaceId) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: workspaceFontsQueryKey(workspaceId) });
    },
  });
}

function workspaceFontsQueryKey(workspaceId: string) {
  return [...WORKSPACE_FONTS_QUERY_KEY_ROOT, workspaceId] as const;
}

/**
 * A list fetch that started before a write can still resolve with the old rows. Wait for it so
 * the following cache update is not overwritten.
 */
async function settleInFlightWorkspaceFontsFetch(queryClient: QueryClient, workspaceId: string): Promise<void> {
  const queryKey = workspaceFontsQueryKey(workspaceId);
  if (queryClient.getQueryState(queryKey)?.fetchStatus !== 'fetching') {
    return;
  }
  await queryClient.fetchQuery({ queryKey, queryFn: () => getWorkspaceFonts(workspaceId) }).catch(() => undefined);
}

function mergeUploadedWorkspaceFonts(
  current: WorkspaceCustomFontDto[] | undefined,
  uploaded: readonly WorkspaceCustomFontDto[],
): WorkspaceCustomFontDto[] {
  const byId = new Map((current ?? []).map((font) => [font.id, font]));
  for (const font of uploaded) {
    byId.set(font.id, font);
  }
  return [...byId.values()];
}
